
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

export const useAppraisalData = (
  personalGoals: any[],
  selectedMemberGoals: any[],
  activeTab: string,
  teamMembers: any[]
) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [supervisorName, setSupervisorName] = useState<string>("");
  const [appraisalMetrics, setAppraisalMetrics] = useState({
    totalAppraisals: 0,
    selfRatingDone: 0,
    managerRatingDone: 0
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { activeYear } = usePerformanceYear();

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error("Error getting user:", userError);
          return;
        }

        const { data: userData, error: userDataError } = await supabase
          .from('users')
          .select('*, supervisor:supervisor_id(full_name)')
          .eq('id', user.id)
          .single();

        if (userDataError) {
          console.error("Error fetching user data:", userDataError);
          return;
        }

        setCurrentUser(userData);
        
        if (userData.supervisor && userData.supervisor.full_name) {
          setSupervisorName(userData.supervisor.full_name);
        }
      } catch (error) {
        console.error("Error loading current user:", error);
      }
    };

    loadCurrentUser();
  }, []);

  const syncSupervisorForGoals = async (goals: any[], targetUserId?: string) => {
    if (!goals || goals.length === 0) return;

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Error getting current user:", userError);
        return;
      }

      if (!targetUserId) {
        // Personal Goals: Get user's supervisor and update appraisals
        const { data: currentUserData, error: currentUserError } = await supabase
          .from('users')
          .select('supervisor_id')
          .eq('id', user.id)
          .single();

        if (currentUserError) {
          console.error("Error fetching current user data:", currentUserError);
          return;
        }

        const supervisorId = currentUserData?.supervisor_id;
        
        if (supervisorId) {
          const goalIds = goals.map(goal => goal.id);
          
          const { error: appraisalsUpdateError } = await supabase
            .from('performance_appraisals')
            .update({ supervisor_id: supervisorId })
            .eq('user_id', user.id)
            .eq('year', activeYear)
            .in('goal_id', goalIds);

          if (appraisalsUpdateError) {
            console.error("Error updating supervisor_id in performance_appraisals:", appraisalsUpdateError);
          }
        }
      } else {
        // Team Member Goals: Current user is supervisor
        const goalIds = goals.map(goal => goal.id);
        
        const { error: appraisalsUpdateError } = await supabase
          .from('performance_appraisals')
          .update({ supervisor_id: user.id })
          .eq('user_id', targetUserId)
          .eq('year', activeYear)
          .in('goal_id', goalIds);

        if (appraisalsUpdateError) {
          console.error("Error updating supervisor_id in performance_appraisals for team member:", appraisalsUpdateError);
        }
      }
    } catch (error) {
      console.error("Error in supervisor sync:", error);
    }
  };

  const loadGoalAppraisals = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      let targetGoals = activeTab === "personal-goals" ? personalGoals : selectedMemberGoals;
      let targetUserId = activeTab === "personal-goals" ? user.id : teamMembers.find(member => member.id)?.id;

      if (!targetGoals || targetGoals.length === 0) {
        return {
          appraisalMap: {},
          savedGoalIds: new Set(),
          savedManagerGoalIds: new Set(),
          ratingsMap: {},
          managerRatingsMap: {},
          commentsMap: {},
          managerCommentsMap: {}
        };
      }

      // Sync supervisor IDs first
      await syncSupervisorForGoals(targetGoals, activeTab === "manage-team-members" ? targetUserId : undefined);

      const goalIds = targetGoals.map(goal => goal.id);
      
      let query = supabase
        .from('performance_appraisals')
        .select('*')
        .eq('year', activeYear)
        .in('goal_id', goalIds);

      if (activeTab === "personal-goals") {
        query = query.eq('user_id', user.id);
      } else {
        query = query.eq('supervisor_id', user.id);
      }

      const { data: appraisals, error } = await query;

      if (error) {
        console.error("Error loading goal appraisals:", error);
        throw error;
      }

      const appraisalMap: {[key: string]: any} = {};
      const savedGoalIds = new Set<string>();
      const savedManagerGoalIds = new Set<string>();
      const ratingsMap: {[key: string]: string} = {};
      const managerRatingsMap: {[key: string]: string} = {};
      const commentsMap: {[key: string]: string} = {};
      const managerCommentsMap: {[key: string]: string} = {};

      appraisals?.forEach(appraisal => {
        if (appraisal.goal_id) {
          appraisalMap[appraisal.goal_id] = appraisal;
          
          if (appraisal.self_rating) {
            const ratingKey = getRatingKey(appraisal.self_rating);
            ratingsMap[appraisal.goal_id] = ratingKey;
            savedGoalIds.add(appraisal.goal_id);
          }
          
          if (appraisal.manager_rating) {
            const managerRatingKey = getRatingKey(appraisal.manager_rating);
            managerRatingsMap[appraisal.goal_id] = managerRatingKey;
            savedManagerGoalIds.add(appraisal.goal_id);
          }
          
          if (appraisal.self_comments) {
            commentsMap[appraisal.goal_id] = appraisal.self_comments;
          }
          
          if (appraisal.manager_comments) {
            managerCommentsMap[appraisal.goal_id] = appraisal.manager_comments;
          }
        }
      });

      return {
        appraisalMap,
        savedGoalIds,
        savedManagerGoalIds,
        ratingsMap,
        managerRatingsMap,
        commentsMap,
        managerCommentsMap
      };
    } catch (error) {
      console.error("Error in loadGoalAppraisals:", error);
      throw error;
    }
  };

  const getRatingKey = (rating: number): string => {
    switch (rating) {
      case 1: return "far-below-expectations";
      case 2: return "below-expectations";
      case 3: return "meets-expectations";
      case 4: return "exceeds-expectations";
      case 5: return "far-exceeds-expectations";
      default: return "";
    }
  };

  const calculateAppraisalMetrics = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return;
      }

      if (activeTab === "manage-team-members") {
        const { data: appraisals, error } = await supabase
          .from('performance_appraisals')
          .select('*')
          .eq('supervisor_id', user.id)
          .eq('year', activeYear);

        if (error) {
          console.error("Error calculating appraisal metrics:", error);
          return;
        }

        const totalAppraisals = appraisals?.length || 0;
        const selfRatingDone = appraisals?.filter(a => a.self_rating !== null).length || 0;
        const managerRatingDone = appraisals?.filter(a => a.manager_rating !== null).length || 0;

        setAppraisalMetrics({
          totalAppraisals,
          selfRatingDone,
          managerRatingDone
        });
      }
    } catch (error) {
      console.error("Error calculating appraisal metrics:", error);
    }
  };

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    currentUser,
    supervisorName,
    appraisalMetrics,
    loadGoalAppraisals,
    calculateAppraisalMetrics,
    refreshTrigger,
    triggerRefresh
  };
};
